import { apiClient } from '../../../api/axios';
import { ApiResponse } from '../../author/services/authorAuth'; // Reusing the generic ApiResponse type

export interface LoginPayload {
    email: string;
    password: string;
}

export interface LoginResponseData {
    username: string;
    role: string;
    authToken: string;
    fullName: string;
    userId: string;
    roleId: number;
    roles?: Record<string, string>; // { "id": "name" }
    profileCompleted: boolean;
    isInitialLogin?: boolean;
}

export interface ProfileData {
    userId: string;
    username: string;
    lastLogin: string;
    lastPasswordChange: string | null;
    isVerified: boolean;
    roleId: number;
    personId: string;
    person: {
        personId: string;
        lastName: string;
        firstName: string;
        otherName: string;
        email: string;
        phoneNo: string;
        address: string;
        active: boolean;
        createdDate: string;
        fullName: string;
        qualificationId: number;
        institutionId: number | null;
        passportUrl: string | null;
        titleId: number;
        sexId: number;
        dob: string | null;
        stateId: number;
        rankId: number;
        profileCompleted: boolean;
    };
    role: {
        id: number;
        name: string;
        active: boolean;
        slug: string;
    };
    active: boolean;
    createdDate: string;
}

export interface ProfileUpdatePayload {
    firstName: string;
    lastName: string;
    otherName: string;
    email: string;
    phoneNo: string;
    titleId: number;
    institutionId: number | null;
    dob?: string | null;
    address: string;
    qualificationId: number;
    sexId: number;
    rankId: number;
    stateId: number;
}

export interface ResetPasswordPayload {
    userId: string;
    newPassword: string;
}

export interface ChangePasswordPayload {
    currentPassword: string;
    newPassword: string;
}

export const authService = {
    login: async (payload: LoginPayload): Promise<ApiResponse<LoginResponseData>> => {
        const apiPayload = {
            username: payload.email,
            password: payload.password
        };
        const response = await apiClient.post('/Auth/Login', apiPayload, { skipAuth: true } as any);
        return response.data;
    },

    switchAccount: async (roleId: number): Promise<ApiResponse<LoginResponseData>> => {
        const response = await apiClient.post(`/Auth/SwitchAccount?roleId=${roleId}`);
        return response.data;
    },

    getActiveRoles: async (): Promise<ApiResponse<Record<string, string>>> => {
        const response = await apiClient.get('/Auth/GetByUserIdActiveRoles');
        return response.data;
    },

    initiatePasswordReset: async (email: string): Promise<ApiResponse> => {
        const response = await apiClient.post(`/Auth/ResetPassword?email=${encodeURIComponent(email)}`, undefined, { skipAuth: true } as any);
        return response.data;
    },

    verifyResetCode: async (code: string): Promise<ApiResponse<string>> => {
        const response = await apiClient.post(`/Auth/ConfirmPasswordResetCode?resetCode=${encodeURIComponent(code)}`, undefined, { skipAuth: true } as any);
        return response.data;
    },

    completePasswordReset: async (payload: ResetPasswordPayload): Promise<ApiResponse> => {
        const response = await apiClient.post('/Auth/ConfirmPasswordResetNewPassword', payload, { skipAuth: true } as any);
        return response.data;
    },

    changePassword: async (payload: ChangePasswordPayload): Promise<ApiResponse> => {
        const response = await apiClient.post('/Auth/ChangePassword', payload);
        return response.data;
    },

    getUserProfile: async (): Promise<ApiResponse<ProfileData>> => {
        const response = await apiClient.get('/Auth/GetUserProfile');
        return response.data;
    },

    updateProfile: async (payload: ProfileUpdatePayload): Promise<ApiResponse> => {
        const response = await apiClient.post('/Auth/UpdateProfile', payload);
        return response.data;
    }
};
